import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "disabled", "tabIndex", "onClick", "isHovered", "onMouseEnter", "onMouseLeave"];
import { useCallback, useRef, useState } from 'react';
import debounce from 'react-utils/debounce';
import styled from 'styled-components';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { AbstractAudioPlayer, PlayerStatuses, usePlayer } from './AbstractAudioPlayer';
import UIIcon from 'UIComponents/icon/UIIcon';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import Tooltip from 'UIComponents/tooltip/UITooltip';
import { formatSeconds } from './utils';
import { useTracker } from '../hooks/useTracker';
import { Flex } from 'foundations-components/components/Flex';
import { Clickable } from 'foundations-components/transitional/components/Clickable';
import { Slider } from 'foundations-components/transitional/components/Slider';
import { Box } from 'foundations-components/transitional/components/Box';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
// adapted from https://git.hubteam.com/HubSpot/media-default-modules/blob/master/jsr-modules/src/components/islands/AudioPlayer.tsx

const SHOW_PAUSE_STATUSES = [PlayerStatuses.PLAYING, PlayerStatuses.LOADING, PlayerStatuses.WAITING];
const AudioPlayerButton = _ref => {
  let {
      children,
      disabled,
      tabIndex,
      onClick,
      isHovered,
      onMouseEnter,
      onMouseLeave
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  return /*#__PURE__*/_jsx(Flex, {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    borderStyle: "hidden",
    padding: "100",
    marginInline: "25",
    onMouseEnter: onMouseEnter,
    onMouseLeave: onMouseLeave,
    backgroundColor: isHovered ? 'fill-tertiary-pressed' : undefined,
    borderRadius: "300",
    blockSize: "40px",
    inlineSize: "40px",
    children: /*#__PURE__*/_jsx(Clickable, Object.assign({
      disabled: disabled,
      tabIndex: tabIndex,
      onClick: onClick
    }, props, {
      children: children
    }))
  });
};
const StatusIcon = ({
  status
}) => {
  if (status === PlayerStatuses.LOADING || status === PlayerStatuses.WAITING) {
    return /*#__PURE__*/_jsx(UILoadingSpinner, {});
  }
  if (status === PlayerStatuses.ENDED) {
    return /*#__PURE__*/_jsx(UIIcon, {
      name: "refresh"
    });
  } else if (status === PlayerStatuses.ERROR) {
    return /*#__PURE__*/_jsx(UIIcon, {
      name: "warning"
    });
  }
  return status === PlayerStatuses.PLAYING ? /*#__PURE__*/_jsx(UIIcon, {
    name: "playerPause"
  }) : /*#__PURE__*/_jsx(UIIcon, {
    name: "playerPlay"
  });
};
const PlayPause = () => {
  const player = usePlayer();
  const tracker = useTracker({
    component: 'AudioPlayer'
  });
  const {
    hasStarted,
    status
  } = player.state;
  const showPause = SHOW_PAUSE_STATUSES.includes(status);
  const [isHovered, setIsHovered] = useState(false);
  const handleMouseEnter = () => {
    setIsHovered(true);
  };
  const handleMouseLeave = () => {
    setIsHovered(false);
  };
  const handleClick = () => {
    if (showPause) {
      player.actions.pause();
      tracker.track('audioPlayerInteraction', {
        action: 'pause'
      });
    } else {
      player.actions.play().catch(console.error);
      tracker.track('audioPlayerInteraction', {
        action: hasStarted ? 'resume' : 'play'
      });
    }
  };
  return /*#__PURE__*/_jsx(Tooltip, {
    title: showPause ? /*#__PURE__*/_jsx(FormattedMessage, {
      message: "audioComponents.player.pause"
    }) : /*#__PURE__*/_jsx(FormattedMessage, {
      message: "audioComponents.player.play"
    }),
    placement: "left",
    children: /*#__PURE__*/_jsx(AudioPlayerButton, {
      "aria-label": showPause ? I18n.text('audioComponents.player.pause') : I18n.text('audioComponents.player.play'),
      "data-test-id": "play-pause-button",
      tabIndex: status === PlayerStatuses.INITIAL || status === PlayerStatuses.ERROR ? -1 : 0,
      onClick: handleClick,
      isHovered: isHovered,
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
      children: /*#__PURE__*/_jsx(StatusIcon, {
        status: status
      })
    })
  });
};
const Seconds = ({
  seconds
}) => {
  return /*#__PURE__*/_jsx(_Fragment, {
    children: seconds < 10 ? `0${seconds}` : `${seconds}`
  });
};
const Minutes = ({
  seconds
}) => {
  const time = formatSeconds(seconds);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [time.minutes, ":", /*#__PURE__*/_jsx(Seconds, {
      seconds: time.seconds
    })]
  });
};
const DurationSpacer = () => /*#__PURE__*/_jsx("span", {
  className: "m-x-1",
  children: "/"
});
const Duration = () => {
  const {
    state
  } = usePlayer();
  if (!state.duration) {
    return null;
  }
  return /*#__PURE__*/_jsxs("div", {
    className: "m-bottom-1",
    children: [/*#__PURE__*/_jsx(Minutes, {
      seconds: Math.floor(state.currentTime)
    }), /*#__PURE__*/_jsx(DurationSpacer, {}), /*#__PURE__*/_jsx(Minutes, {
      seconds: Math.floor(state.duration)
    })]
  });
};
const CurrentTimeSlider = () => {
  const player = usePlayer();
  const tracker = useTracker();
  const trackSeek = debounce(useCallback(() => {
    tracker.track('audioPlayerInteraction', {
      action: 'seek'
    });
  }, [tracker]), 500);
  const handleSeek = useCallback(e => {
    player.actions.seek(Number(e.target.value));
    trackSeek();
  }, [player, trackSeek]);
  const handleKeyDown = useCallback(e => {
    if (e.key === ' ' && player.state.status !== PlayerStatuses.PAUSED) {
      player.actions.pause();
    } else if (e.key === ' ' && player.state.status === PlayerStatuses.PAUSED) {
      player.actions.play().catch(console.error);
    }
  }, [player]);
  return player.state.duration ? /*#__PURE__*/_jsx(Tooltip, {
    title: I18n.text('audioComponents.player.seekBarSlider'),
    children: /*#__PURE__*/_jsx(Box, {
      grow: "1",
      marginBlockStart: "100",
      marginInline: "500",
      children: /*#__PURE__*/_jsx(Slider, {
        "aria-label": I18n.text('audioComponents.player.seekBarSlider'),
        min: 0,
        max: Math.ceil(player.state.duration),
        step: 1,
        value: player.state.currentTime,
        onChange: handleSeek,
        onKeyDown: handleKeyDown
      })
    })
  }) : /*#__PURE__*/_jsx(Box, {
    grow: "1"
  });
};
const StyledVolumeControls = styled.div.withConfig({
  displayName: "AudioPlayer__StyledVolumeControls",
  componentId: "sc-1c48hox-0"
})(["display:flex;align-items:center;&[aria-expanded='true']{flex-basis:30%;}"]);
const Volume = ({
  onExpandedChange
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const wrapperRef = useRef(null);
  const player = usePlayer();
  const tracker = useTracker();
  const {
    isMuted
  } = player.state;
  const [isHovered, setIsHovered] = useState(false);
  const handleMouseEnter = () => {
    setIsHovered(true);
  };
  const handleMouseLeave = () => {
    setIsHovered(false);
  };
  const handleChange = e => {
    player.actions.setVolume(Number(e.target.value));
    tracker.track('audioPlayerInteraction', {
      action: 'change-volume'
    });
  };
  const handleClick = () => {
    if (!isExpanded) {
      setIsExpanded(true);
      onExpandedChange('volume');
    } else {
      onExpandedChange(null);
      player.actions.setMuted(!isMuted);
      tracker.track('audioPlayerInteraction', {
        action: isMuted ? 'unmute' : 'mute'
      });
    }
  };
  const handleBlur = e => {
    if (!document.hasFocus()) {
      return;
    }
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsExpanded(false);
      onExpandedChange(null);
    }
  };
  const label = isMuted ? I18n.text('audioComponents.player.unmute') : I18n.text('audioComponents.player.mute');
  return /*#__PURE__*/_jsxs(StyledVolumeControls, {
    ref: wrapperRef,
    "aria-label": I18n.text('audioComponents.player.volumeControls'),
    "aria-expanded": isExpanded,
    role: "group",
    onBlur: handleBlur,
    children: [/*#__PURE__*/_jsx(Tooltip, {
      title: label,
      placement: "right",
      children: /*#__PURE__*/_jsx(AudioPlayerButton, {
        "aria-label": isExpanded ? label : I18n.text('audioComponents.player.volume'),
        "data-test-id": "volume-button",
        "aria-live": "assertive",
        onClick: handleClick,
        isHovered: isHovered,
        onMouseEnter: handleMouseEnter,
        onMouseLeave: handleMouseLeave,
        children: isMuted ? /*#__PURE__*/_jsx(UIIcon, {
          name: "volumeMute"
        }) : /*#__PURE__*/_jsx(UIIcon, {
          name: "volumeUp"
        })
      })
    }), isExpanded && /*#__PURE__*/_jsx(Tooltip, {
      title: I18n.text('audioComponents.player.volume'),
      children: /*#__PURE__*/_jsx(Box, {
        grow: "1",
        marginBlockStart: "100",
        marginInline: "200",
        children: /*#__PURE__*/_jsx(Slider, {
          min: 0,
          max: 1,
          step: 0.05,
          value: isMuted ? 0 : player.state.volume,
          onChange: handleChange
        })
      })
    })]
  });
};
const PLAYBACK_RATES = [0.5, 1, 1.2, 1.5, 2];
const StyledPlaybackRate = styled.div.withConfig({
  displayName: "AudioPlayer__StyledPlaybackRate",
  componentId: "sc-1c48hox-1"
})(["display:flex;align-items:center;"]);
const PlaybackRate = ({
  onExpandedChange
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const wrapperRef = useRef(null);
  const tracker = useTracker();
  const player = usePlayer();
  const {
    setPlaybackRate
  } = player.actions;
  const {
    playbackRate
  } = player.state;
  const currentIndex = PLAYBACK_RATES.indexOf(playbackRate);
  const [isHovered, setIsHovered] = useState(false);
  const handleMouseEnter = () => {
    setIsHovered(true);
  };
  const handleMouseLeave = () => {
    setIsHovered(false);
  };
  const handleBlur = e => {
    if (!document.hasFocus()) {
      return;
    }
    if (!e.currentTarget.contains(e.relatedTarget)) {
      onExpandedChange(null);
      // give enough time before collapsing so clicking on volume works (it moves to the right as playback rate collapses)
      setTimeout(() => setIsExpanded(false), 100);
    }
  };
  return /*#__PURE__*/_jsx(StyledPlaybackRate, {
    ref: wrapperRef,
    "aria-expanded": isExpanded,
    "aria-label": I18n.text('audioComponents.player.playbackRate'),
    role: "group",
    onBlur: handleBlur,
    children: isExpanded ? /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(Tooltip, {
        title: I18n.text('audioComponents.player.slowDownPlayback'),
        placement: "right",
        children: /*#__PURE__*/_jsx(AudioPlayerButton, {
          "aria-label": I18n.text('audioComponents.player.slowDownPlayback'),
          disabled: currentIndex <= 0,
          onClick: () => {
            tracker.track('audioPlayerInteraction', {
              action: 'slow-down'
            });
            setPlaybackRate(PLAYBACK_RATES[currentIndex - 1]);
          },
          isHovered: isHovered,
          onMouseEnter: handleMouseEnter,
          onMouseLeave: handleMouseLeave,
          children: /*#__PURE__*/_jsx(UIIcon, {
            name: "left"
          })
        })
      }), /*#__PURE__*/_jsxs("div", {
        children: [playbackRate, "x"]
      }), /*#__PURE__*/_jsx(Tooltip, {
        title: I18n.text('audioComponents.player.speedUpPlayback'),
        placement: "right",
        children: /*#__PURE__*/_jsx(AudioPlayerButton, {
          "aria-label": I18n.text('audioComponents.player.speedUpPlayback'),
          disabled: currentIndex >= PLAYBACK_RATES.length - 1,
          onClick: () => {
            setPlaybackRate(PLAYBACK_RATES[currentIndex + 1]);
            tracker.track('audioPlayerInteraction', {
              action: 'speed-up'
            });
          },
          isHovered: isHovered,
          onMouseEnter: handleMouseEnter,
          onMouseLeave: handleMouseLeave,
          children: /*#__PURE__*/_jsx(UIIcon, {
            name: "right"
          })
        })
      })]
    }) : /*#__PURE__*/_jsx(Tooltip, {
      title: I18n.text('audioComponents.player.playbackRate'),
      placement: "right",
      children: /*#__PURE__*/_jsxs(AudioPlayerButton, {
        "aria-label": I18n.text('audioComponents.player.playbackRate'),
        "data-test-id": "playback-rate",
        onClick: () => {
          onExpandedChange('playback-rate');
          setIsExpanded(true);
        },
        isHovered: isHovered,
        onMouseEnter: handleMouseEnter,
        onMouseLeave: handleMouseLeave,
        children: [playbackRate, "x"]
      })
    })
  });
};
const PlayerWrapper = ({
  backgroundColor,
  title,
  hideVolumeControls,
  hidePlaybackRateControls
}) => {
  const [expandedSubpanel, setExpandedSubpanel] = useState(null);
  const player = usePlayer();
  const {
    status
  } = player.state;
  const handleExpandedChange = panel => {
    setExpandedSubpanel(panel);
  };
  return /*#__PURE__*/_jsxs(Flex, {
    "aria-label": title ? `${I18n.text('audioComponents.player.playAudio')}: ${title}` : I18n.text('audioComponents.player.playAudio'),
    "data-status": status.toLowerCase(),
    "data-subpanel-expanded": expandedSubpanel,
    "data-test-id": "audio-player",
    role: "group",
    tabIndex: status === PlayerStatuses.INITIAL ? 0 : -1,
    display: "flex",
    flexWrap: "nowrap",
    flexGrow: 1,
    alignItems: "center",
    justifyContent: "space-between",
    minInlineSize: "250px",
    blockSize: "60px",
    paddingInline: "200",
    backgroundColor: backgroundColor || 'fill-tertiary-hover',
    borderRadius: "300",
    children: [/*#__PURE__*/_jsx(PlayPause, {}), /*#__PURE__*/_jsx(Duration, {}), /*#__PURE__*/_jsx(CurrentTimeSlider, {}), !hideVolumeControls && /*#__PURE__*/_jsx(Volume, {
      onExpandedChange: handleExpandedChange
    }), !hidePlaybackRateControls && /*#__PURE__*/_jsx(PlaybackRate, {
      onExpandedChange: handleExpandedChange
    })]
  });
};
const AudioPlayer = ({
  autoPlay,
  backgroundColor,
  duration,
  fileUrl,
  title,
  hideVolumeControls,
  hidePlaybackRateControls
}) => {
  return /*#__PURE__*/_jsx(AbstractAudioPlayer, {
    autoPlay: autoPlay,
    src: fileUrl,
    duration: duration,
    children: /*#__PURE__*/_jsx(PlayerWrapper, {
      backgroundColor: backgroundColor,
      title: title,
      hideVolumeControls: hideVolumeControls,
      hidePlaybackRateControls: hidePlaybackRateControls
    })
  });
};
export default AudioPlayer;